<template>
    <div class="carousel-frame">
        <div :id="slideContainerId" class="carousel-frame-content">
            <blank-slide class="to-be-kept"></blank-slide>
        </div>
        <div class="carousel-button-panel" :class="{ 'hidden' : slide < 1 }">
            <prev-icon
                class="small-icon theme-fio-button"
                :aria-label="$t('carousel.prevButtonLabel')"
                role="button"
                tabindex="0"
                @click="doSlideChange(-1, true)"
                @keypress.enter="doSlideChange(-1, true)"
                @keypress.space="doSlideChange(-1, true)"></prev-icon>
            <resume-icon
                v-if="isAnimPaused"
                class="small-icon theme-fio-button"
                :aria-label="$t('carousel.resumeButtonLabel')"
                role="button"
                tabindex="0"
                @click="onSlidePlayPause"
                @keypress.enter="onSlidePlayPause"
                @keypress.space="onSlidePlayPause"></resume-icon>
            <pause-icon
                v-if="!isAnimPaused"
                class="small-icon theme-fio-button"
                :aria-label="$t('carousel.pauseButtonLabel')"
                role="button"
                tabindex="0"
                @click="onSlidePlayPause"
                @keypress.enter="onSlidePlayPause"
                @keypress.space="onSlidePlayPause"></pause-icon>
            <next-icon
                class="small-icon theme-fio-button"
                :aria-label="$t('carousel.nextButtonLabel')"
                role="button"
                tabindex="0"
                @click="doSlideChange(1, true)"
                @keypress.enter="doSlideChange(1, true)"
                @keypress.space="doSlideChange(1, true)"></next-icon>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";

import { getIntroAnimSlide } from "@/services/slide-services";

import BlankSlide from "@/scalar/blank-slide.svg";
import NextIcon from "@/scalar/next-icon.svg";
import PauseIcon from "@/scalar/pause-icon.svg";
import PrevIcon from "@/scalar/prev-icon.svg";
import ResumeIcon from "@/scalar/resume-icon.svg";

const { locale } = useI18n();

withDefaults(defineProps<{
    idList?: string;
}>(), {
    idList: ""
});

const SlideTimes = [2300, 6000, 6000, 12000, 6000];

// Tracked reactive state
const slideContainerId = ref("");
const slide = ref(0);
const pauseTime = ref<number | undefined>(undefined);

// Untracked per-instance state (not reactive — DOM manipulation manages these)
let timerId: number | undefined;
let startTime: number | undefined;
let timeOnPause: number | undefined;

const isAnimPaused = computed(() => typeof pauseTime.value !== "undefined");

function doSlideChange(offset: number, userReq: boolean): void {
    const now = new Date().getTime();
    if (userReq && offset < 0 && !isAnimPaused.value && (now - (startTime ?? 0)) > 1000) { offset = 0; }

    let nextSlide = slide.value + offset;
    if (nextSlide < 1 || nextSlide >= SlideTimes.length) { nextSlide = 1; }

    if (userReq) { // React immediately to user request and clear current anim
        const scEl = document.getElementById(slideContainerId.value);
        const count = scEl.childElementCount;
        for (let i = 0; i < count; i++) {
            const child = scEl.children[i] as SVGElement;
            if (child.classList.contains("to-be-kept")) {
                child.style.display = "";
                continue;
            }
            child.style.display = "none";
            scEl.removeChild(child);
        }
    }

    startSlide(nextSlide);
}

function onSlidePlayPause(): void {
    const scEl = document.getElementById(slideContainerId.value);
    const svgEl = scEl.querySelector("svg:not(.to-be-kept)");
    if (svgEl === null) { return; }

    const animElements = svgEl.querySelectorAll(".has-anim");
    if (isAnimPaused.value) {
        const now = new Date().getTime();
        const timeUsed = now - (pauseTime.value ?? 0);
        pauseTime.value = undefined;

        // Kill any "next slide" timer
        if (typeof timerId !== "undefined") {
            window.clearTimeout(timerId);
            timerId = undefined;
        }

        // Calculate time remaining
        if (typeof timeOnPause === "undefined") {
            timeOnPause = timeUsed;
        } else {
            timeOnPause += timeUsed;
        }

        const timeRemaining = now - ((startTime ?? 0) + (timeOnPause ?? 0));

        // Resume playback
        const count = animElements.length;
        for (let index = 0; index < count; index++) {
            const animElement = animElements[index] as SVGElement;
            animElement.style.animationPlayState = "running";
        }

        // Setup the "next slide" timeout
        if (timeRemaining > 0) {
            timerId = window.setTimeout(() => { onSlideChangeTimeout(); }, timeRemaining);
        }
    } else {
        // Pause playback
        pauseTime.value = new Date().getTime();

        const count = animElements.length;
        for (let index = 0; index < count; index++) {
            const animElement = animElements[index] as SVGElement;
            animElement.style.animationPlayState = "paused";
        }
    }
}

// Replaces @Watch("slide")
watch(slide, (newSlideNo: number) => {
    if (newSlideNo < 1) {
        const scEl = document.getElementById(slideContainerId.value);
        if (scEl !== null) {
            const blankEl: SVGElement = scEl.querySelector("svg.to-be-kept");
            blankEl.style.display = "";

            const svgEl = scEl.querySelector("svg:not(.to-be-kept)");
            if (svgEl !== null) { scEl.removeChild(svgEl); }
        }
        return;
    }

    const promise = getIntroAnimSlide(newSlideNo, locale.value);
    promise.then(resp => {
        const scEl = document.getElementById(slideContainerId.value);
        if (scEl === null) { return; }

        const scElCount = scEl.childElementCount;
        for (let i = 0; i < scElCount; i++) {
            const child = scEl.children[i] as SVGElement;
            if (child.classList.contains("to-be-kept")) {
                child.style.display = "none";
                continue;
            }
            child.style.display = "";
            scEl.removeChild(child);
        }

        const svgEl = resp.data.querySelector("svg");
        if (isAnimPaused.value) {
            const animElements = svgEl.querySelectorAll(".has-anim");
            const count = animElements.length;
            for (let index = 0; index < count; index++) {
                const animElement = animElements[index] as SVGElement;
                animElement.style.animationPlayState = "paused";
            }
        }

        scEl.appendChild(svgEl);
    });
});

// Replaces $watch("$i18n.locale") in beforeMount — reload current slide on locale change
watch(locale, (newLocale: string, oldLocale: string) => {
    if (newLocale === oldLocale) { return; }

    // Reset existing slide on language change
    if (slide.value < 0) { return; } // In transition, no need to restart
    const slideNo = slide.value > 1 ? slide.value : 1;
    startSlide(slideNo);
});

function onSlideChangeTimeout(): void {
    if (isAnimPaused.value) { return; } // paused

    doSlideChange(1, false);
}

function startSlide(slideNo: number): void {
    if (typeof timerId !== "undefined") {
        window.clearTimeout(timerId);
        timerId = undefined;
    }

    startTime = new Date().getTime();

    // Handle paused animations
    if (isAnimPaused.value) {
        pauseTime.value = startTime;
        timeOnPause = 0;
    } else {
        pauseTime.value = undefined;
        timeOnPause = undefined;
    }

    timerId = window.setTimeout(() => { onSlideChangeTimeout(); }, SlideTimes[slideNo]);

    // Special case for resetting an existing slide
    if (slide.value === slideNo) {
        slide.value = -1; // Force the watch function to trigger
        setTimeout(() => { slide.value = slideNo; }, 10);
        return;
    }

    slide.value = slideNo;
}

onMounted(() => {
    slide.value = 0;

    const tmpArray = new Uint32Array(1);
    window.crypto.getRandomValues(tmpArray);
    slideContainerId.value = "scid-" + tmpArray[0].toString(16);

    startSlide(0);
});

onBeforeUnmount(() => {
    if (typeof timerId !== "undefined") {
        window.clearTimeout(timerId);
        timerId = undefined;
    }
});
</script>

<style scoped>
.carousel-frame {
    border: 1px solid var(--outline-colour);
    width: 95%;
    min-height: min(70%,42vw);
    margin: 1em;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    user-select: none;
    cursor: unset;
}

.carousel-frame-content {
    width: 100%;
}

.carousel-button-panel {
    display: flex;
    position: fixed;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
}

.carousel-button-panel > .theme-fio-button {
    opacity: 0.02;
}

.carousel-button-panel > .theme-fio-button:hover,
.carousel-button-panel > .theme-fio-button:active {
    opacity: 1.0;
}

.hidden {
    opacity: 0.0;
}
</style>
